import type { PrismScope, PrismCitation, PrismChatMeta, PrismWorkflowCard } from "@/lib/prism/types";
import type { PrismModelId } from "@/lib/prism/catalog";

export type FocusDockPosition = "left" | "right" | "top" | "bottom";

/** User-authored skill saved locally to the workspace state. */
export interface PrismCustomSkill {
    id: string;
    name: string;
    description: string;
    promptTemplate: string;
    createdAt: number;
}

export type PrismStepStatus = "todo" | "doing" | "done";

/** One stage in a project's pipeline. May be linked to a skill to run. */
export interface PrismProjectStep {
    id: string;
    title: string;
    /** Optional builtin/custom skill id this step runs. */
    skillId?: string;
    status: PrismStepStatus;
}

/** A project groups chats and defines a repeatable pipeline of steps. */
export interface PrismProject {
    id: string;
    name: string;
    description?: string;
    createdAt: number;
    steps: PrismProjectStep[];
}

/** Default starter pipeline so a new project is never empty. */
export function defaultProjectSteps(): PrismProjectStep[] {
    const mk = (title: string, skillId?: string): PrismProjectStep => ({
        id: crypto.randomUUID(),
        title,
        skillId,
        status: "todo",
    });
    return [
        mk("Frame the objective"),
        mk("Gather knowledge & signals", "company-deep-dive"),
        mk("Assess what needs attention", "portfolio-attention"),
        mk("Decide & draft the output"),
    ];
}

export interface PrismChatMessage {
    role: "user" | "assistant";
    content: string;
    time: string;
    cards?: PrismWorkflowCard[];
    citations?: PrismCitation[];
    meta?: PrismChatMeta | null;
    dispatchedModule?: string | null;
}

export interface PrismChatSession {
    id: string;
    title: string;
    messages: PrismChatMessage[];
    /** Pinned chats sort to the top of Recents. */
    pinned: boolean;
    lastUpdated: number;
    projectId?: string | null;
}

export interface PrismUiState {
    schemaVersion: 1;
    dockPosition: FocusDockPosition;
    focusCollapsed: boolean;
    scope: PrismScope;
    activeSessionId: string | null;
    sessions: PrismChatSession[];
    selectedModelId: PrismModelId;
    customSkills: PrismCustomSkill[];
    projects: PrismProject[];
}

export const PRISM_STATE_BLOB_TYPE = "prism_state";

export const DEFAULT_PRISM_UI_STATE: PrismUiState = {
    schemaVersion: 1,
    dockPosition: "right",
    focusCollapsed: false,
    scope: "fund",
    activeSessionId: null,
    sessions: [],
    selectedModelId: "pro",
    customSkills: [],
    projects: [],
};

export function createPrismSession(
    title = "New chat",
    projectId: string | null = null,
): PrismChatSession {
    return {
        id: crypto.randomUUID(),
        title,
        messages: [],
        pinned: false,
        lastUpdated: Date.now(),
        projectId,
    };
}

export function sessionTitleFromMessage(message: string): string {
    const trimmed = message.trim().replace(/\s+/g, " ");
    if (!trimmed) return "New chat";
    return trimmed.length > 48 ? `${trimmed.slice(0, 48)}…` : trimmed;
}
